import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../../components/PageContainer';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { ErrorDisplay } from '../../components/ui/ErrorDisplay';
import { Button } from '../../components/ui/Button';
import type { UserRole } from '@wineryxplorer/shared';
import { AdminCreateUserModal } from './AdminCreateUserModal';
import { AdminUsersFilters } from './AdminUsersFilters';
import { RoleBadge, UserAvatar } from './UserIdentity';

const PAGE_SIZE = 25;

const SORTABLE_COLUMNS = ['name', 'userName', 'email', 'createdAt'] as const;
type SortableColumn = (typeof SORTABLE_COLUMNS)[number];

const COLUMN_LABELS: Record<SortableColumn, string> = {
  name: 'Name',
  userName: 'Username',
  email: 'Email',
  createdAt: 'Joined',
};

export const AdminUsers = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [status, setStatus] = useState<'active' | 'deactivated' | 'all'>('all');
  const [emailVerified, setEmailVerified] = useState<'' | 'true' | 'false'>('');
  const [sortBy, setSortBy] = useState<SortableColumn>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  // Debounce search input
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 300);
    setDebounceTimer(timer);
  };

  const { data, isLoading, error, refetch } = trpc.adminUsers.list.useQuery({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch || undefined,
    role: role || undefined,
    status,
    emailVerified: emailVerified === '' ? undefined : emailVerified === 'true',
    sortBy,
    sortOrder,
  });

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleFilterChange = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
    setter(value);
    setPage(0);
  };

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  const showingFrom = data && data.total > 0 ? page * PAGE_SIZE + 1 : 0;
  const showingTo = data ? Math.min((page + 1) * PAGE_SIZE, data.total) : 0;

  const SortHeader = ({ column }: { column: SortableColumn }) => {
    const isActive = sortBy === column;
    return (
      <button
        onClick={() => handleSort(column)}
        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
      >
        {COLUMN_LABELS[column]}
        {isActive && (
          <span className="text-primary-600">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>
        )}
      </button>
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoading message="Loading users..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorDisplay
          title="Failed to load users"
          message={error.message}
          showRetry
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-2">
              Manage user accounts, roles, and permissions.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)}>Create user</Button>
        </div>

        {/* Filters */}
        <AdminUsersFilters
          search={search}
          onSearchChange={handleSearchChange}
          role={role}
          onRoleChange={(value) => handleFilterChange(setRole, value)}
          status={status}
          onStatusChange={(value) => handleFilterChange(setStatus, value)}
          emailVerified={emailVerified}
          onEmailVerifiedChange={(value) => handleFilterChange(setEmailVerified, value)}
        />

        {/* User list */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="col-span-4">
              <SortHeader column="name" />
            </div>
            <div className="col-span-3">
              <SortHeader column="email" />
            </div>
            <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </div>
            <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </div>
            <div className="col-span-2">
              <SortHeader column="createdAt" />
            </div>
          </div>

          {/* Rows */}
          {!data || data.items.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No users found matching your filters.
            </div>
          ) : (
            data.items.map((user) => (
              <div
                key={user._id}
                onClick={() => navigate(`/admin/users/${user._id}`)}
                className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${user.deactivated ? 'opacity-60' : ''}`}
              >
                {/* Name + avatar + username */}
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <UserAvatar name={user.name} image={user.image} avatar={user.avatar} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.name || 'Unnamed'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">@{user.userName}</div>
                  </div>
                </div>

                {/* Email */}
                <div className="col-span-3 flex items-center min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm text-gray-600 truncate">{user.email}</span>
                    {user.emailVerified ? (
                      <span title="Email verified" className="text-green-500 flex-shrink-0">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : (
                      <span title="Email not verified" className="text-gray-300 flex-shrink-0">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>

                {/* Role */}
                <div className="col-span-2 flex items-center">
                  <RoleBadge role={user.role} />
                </div>

                {/* Status */}
                <div className="col-span-1 flex items-center">
                  {user.deactivated ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                      Inactive
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  )}
                </div>

                {/* Joined date */}
                <div className="col-span-2 flex items-center text-sm text-gray-500">
                  {new Date(user.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between px-2">
            <div className="text-sm text-gray-600">
              Showing {showingFrom} to {showingTo} of {data.total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <AdminCreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}
    </PageContainer>
  );
};
